import React, { useEffect, useState } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify, FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { IoMdNotificationsOutline } from "react-icons/io";
import { MdOpenInNew } from "react-icons/md";
import { supabase } from "lib/supabaseClient";

const getAvatarCacheKey = (url) =>
  url ? `core_avatar_cache_${btoa(url).slice(0, 120)}` : null;

const readCachedAvatar = (url) => {
  const key = getAvatarCacheKey(url);
  if (!key) return null;
  try {
    return localStorage.getItem(key) || sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeCachedAvatar = (url, dataUrl) => {
  const key = getAvatarCacheKey(url);
  if (!key || !dataUrl) return;
  try {
    localStorage.setItem(key, dataUrl);
  } catch {
    try {
      sessionStorage.setItem(key, dataUrl);
    } catch {}
  }
};

const blobToDataUrl = (blob) =>
  new Promise((resolve) => {
    try {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result || null);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    } catch {
      resolve(null);
    }
  });

const fetchAvatarBlob = async (url) => {
  if (!url) return null;
  try {
    if (url.includes("supabase.co/storage/v1/object/public/")) {
      const path = url.split("/public/")[1];
      const bucket = path.split("/")[0];
      const filePath = path.split("/").slice(1).join("/");
      const { data, error } = await supabase.storage
        .from(bucket)
        .download(filePath);
      if (!error && data) return data;
    }
    const response = await fetch(url, { cache: "force-cache" });
    return await response.blob();
  } catch {
    return null;
  }
};

const SafeAvatar = React.memo(({ url, wrapperClassName, imageClassName }) => {
  const [cachedUrl, setCachedUrl] = useState(() => readCachedAvatar(url));
  const [objectUrl, setObjectUrl] = useState(null);

  useEffect(() => {
    setCachedUrl(readCachedAvatar(url));
  }, [url]);

  useEffect(() => {
    let isActive = true;
    let nextObjectUrl;
    const load = async () => {
      if (!url) {
        setObjectUrl(null);
        return;
      }
      const blob = await fetchAvatarBlob(url);
      if (!isActive) return;
      if (blob) {
        nextObjectUrl = URL.createObjectURL(blob);
        setObjectUrl(nextObjectUrl);
        const dataUrl = await blobToDataUrl(blob);
        if (dataUrl && isActive) {
          writeCachedAvatar(url, dataUrl);
          setCachedUrl(dataUrl);
        }
      }
    };
    load();
    return () => {
      isActive = false;
      if (nextObjectUrl) URL.revokeObjectURL(nextObjectUrl);
    };
  }, [url]);

  const displayUrl =
    objectUrl || cachedUrl || "https://i.ibb.co/mRpg6P0/default-avatar.png";
  return (
    <div
      className={`relative select-none ${wrapperClassName}`}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div className="absolute inset-0 z-10 bg-transparent" />
      <div
        className={imageClassName}
        style={{
          backgroundImage: `url(${displayUrl})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          pointerEvents: "none",
        }}
      />
    </div>
  );
});

const Navbar = (props) => {
  const { onOpenSidenav, brandText, userProfile: userProfileProp, isUserDataLoading: isLoadingProp } = props;
  const navigate = useNavigate();
  const [userProfile, setUserProfile] = useState(() => {
    if (userProfileProp) return userProfileProp;
    try {
      const s = sessionStorage.getItem("core_admin_user_profile");
      if (s) return JSON.parse(s);
      const l = localStorage.getItem("core_admin_user_profile");
      return l ? JSON.parse(l) : null;
    } catch {
      return null;
    }
  });
  const [isLoading, setIsLoading] = useState(() => {
    if (typeof isLoadingProp === "boolean") return isLoadingProp;
    return !userProfile;
  });
  const [searchQuery, setSearchQuery] = useState("");
  // eslint-disable-next-line no-unused-vars
  const [searchResults, setSearchResults] = useState([]);
  useEffect(() => {
    if (userProfileProp) {
      setUserProfile(userProfileProp);
      setIsLoading(typeof isLoadingProp === "boolean" ? isLoadingProp : false);
    }
  }, [userProfileProp, isLoadingProp]);

  // ... (نفس منطق البحث المكتوب لديك يبقى كما هو)

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth/sign-in");
  };

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-2xl border border-gray-200 bg-white/80 p-4 shadow-xl backdrop-blur-xl transition-all">
      <div className="ml-[6px]">
        <div className="pt-1 text-left">
          <div className="flex flex-col">
            {/* نظام التتبع (Breadcrumbs) بأسلوب رشيق */}
            <p
              className="mb-1 text-[9px] font-light uppercase  tracking-[0.2em] text-gray-600"
              style={{ fontFamily: "'Kufam', sans-serif" }}
            >
              CORE <span className="mx-1 font-normal text-core-accent">/</span>{" "}
              {brandText}
            </p>

            <div className="flex min-h-[45px] items-center">
              {isLoading ? (
                <div className="animate-pulse space-y-2">
                  <div className="h-5 w-32 rounded-md bg-gray-200"></div>
                </div>
              ) : (
                <div className="animate-fadeIn">
                  <h1 className="text-2xl font-light uppercase  leading-none tracking-wider text-core-charcoal">
                    Hello,{" "}
                    <span className="font-normal text-core-accent drop-shadow-[0_0_10px_rgba(50,1,233,0.3)]">
                      {userProfile?.full_name?.split(" ")[0] || "Professional"}
                    </span>
                  </h1>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="relative flex h-[61px] w-[400px] items-center justify-around gap-2 rounded-2xl border border-gray-200 bg-white/50 px-3 backdrop-blur-md">
        {/* حقل البحث - Deep Space Style */}
        <div className="group relative flex h-10 items-center rounded-xl border border-gray-200 bg-white text-core-charcoal transition-all focus-within:border-core-accent/50 xl:w-[260px]">
          <p className="pl-3 pr-2 text-xl text-gray-500 transition-colors group-focus-within:text-core-accent">
            <FiSearch className="h-4 w-4" />
          </p>
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="SEARCH OPERATIONS..."
            className="block h-full w-full bg-transparent text-[10px] font-light uppercase tracking-widest text-core-charcoal outline-none placeholder:text-gray-400"
          />

          {/* نتائج البحث المحدثة بتصميم زجاجي */}
          {searchResults.length > 0 && (
            <div className="absolute left-0 top-[55px] z-[100] w-[350px] rounded-2xl border border-gray-200 bg-white p-4 shadow-[0_20px_50px_rgba(0,0,0,0.1)] backdrop-blur-2xl md:w-[400px]">
              <p className="mb-4 ml-2 text-[8px] font-light uppercase  tracking-[0.3em] text-gray-500">
                Intelligence Results
              </p>
              <div className="flex flex-col gap-1">
                {searchResults.map((emp) => (
                  <div
                    key={emp.id}
                    onClick={() => {
                      navigate(
                        `/admin/profile?name=${encodeURIComponent(
                          emp.full_name
                        )}`
                      );
                      setSearchQuery("");
                    }}
                    className="flex cursor-pointer items-center gap-4 rounded-xl border border-transparent p-3 transition-all hover:border-gray-100 hover:bg-gray-50"
                  >
                    <SafeAvatar
                      url={emp.avatar_url}
                      wrapperClassName="h-9 w-9"
                      imageClassName="h-full w-full rounded-lg border border-gray-200"
                    />
                    <div className="flex-1 overflow-hidden">
                      <p className="text-[11px] font-normal uppercase tracking-wide text-core-charcoal">
                        {emp.full_name}
                      </p>
                      <p className="text-[9px] font-light uppercase tracking-tight text-core-accent">
                        Unit: #{emp.employee_number}
                      </p>
                    </div>
                    <MdOpenInNew
                      className="text-gray-400 transition-all group-hover:text-core-accent"
                      size={14}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* قائمة التنبيهات والبروفايل */}
        <div className="flex items-center gap-3">
          <span
            className="flex cursor-pointer text-xl text-core-charcoal xl:hidden"
            onClick={onOpenSidenav}
          >
            <FiAlignJustify className="h-5 w-5" />
          </span>

          <Dropdown
            button={
              <IoMdNotificationsOutline className="h-5 w-5 cursor-pointer text-gray-500 transition-all hover:text-core-accent" />
            }
            children={
              <div className="flex w-[300px] flex-col gap-3 rounded-2xl border border-gray-200 bg-white p-4 shadow-2xl backdrop-blur-xl">
                <p className="text-[9px] font-light uppercase tracking-widest text-core-charcoal">
                  Intelligence Feed
                </p>
                <div className="h-px w-full bg-gray-100" />
                <div className="py-6 text-center text-[10px] uppercase  tracking-widest text-gray-400">
                  No operational alerts.
                </div>
              </div>
            }
            classNames={"py-2 top-4 -left-[230px] md:-left-[440px] w-max"}
          />

          <Dropdown
            button={
              <div className="h-9 w-9 rounded-xl border border-gray-200 p-[2px] shadow-sm transition-all hover:border-core-accent active:scale-95">
                <SafeAvatar
                  url={userProfile?.avatar_url}
                  wrapperClassName="h-full w-full"
                  imageClassName="bg-gray-100 h-full w-full rounded-lg"
                />
              </div>
            }
            children={
              <div className="flex w-64 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl backdrop-blur-2xl">
                <div className="bg-gray-50 p-5">
                  <p className="text-xs font-normal uppercase tracking-widest text-core-charcoal">
                    {userProfile?.full_name || "Admin User"}
                  </p>
                  <p className="mt-1 text-[9px] font-light uppercase tracking-[0.2em] text-core-accent">
                    {userProfile?.job_title || "Operational Unit"}
                  </p>
                </div>
                <div className="h-px w-full bg-gray-100" />
                <div className="p-2">
                  <button
                    onClick={handleLogout}
                    className="w-full rounded-xl p-3 text-left text-[9px] font-light uppercase tracking-[0.3em] text-red-500 transition-all hover:bg-red-50"
                  >
                    Terminate Session
                  </button>
                </div>
              </div>
            }
            classNames={"py-2 top-8 -left-[200px] w-max"}
          />
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
